/**
 * In-memory state for the Magic Capture prototype flow.
 *
 * Mirrors the Mini PRD's review gate: no action lands in the Actions system
 * without explicit carer acceptance (accept / reject / tweak per action).
 *
 * Also emits the Mini PRD's instrumentation events (must-have, not polish)
 * into an in-memory log so the exact schema is visible in the dev events
 * panel - the real build wires these same events to PostHog.
 */
import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type CapturedDocument,
  type CaptureMethod,
  type SuggestedAction,
} from './fixtures';

export type ActionDecision = 'pending' | 'accepted' | 'rejected';

export type EntrySurface = 'home' | 'care' | 'journey-prompt';

export interface ReviewedAction extends SuggestedAction {
  decision: ActionDecision;
  /** Set when the carer edits the title before accepting ("tweak"). */
  isTweaked: boolean;
}

export interface SavedCapture {
  id: string;
  doc: CapturedDocument;
  method: CaptureMethod;
  acceptedActions: ReviewedAction[];
  savedAt: string;
  markedWrong: boolean;
}

export interface TrackedEvent {
  name: string;
  payload: Record<string, string | number | boolean>;
  at: string;
}

interface CaptureFlowState {
  /** Document going through the pipeline right now. */
  doc: CapturedDocument | null;
  method: CaptureMethod;
  /** Pages snapped in the multi-shot session: JPEG data URL when the real
   * camera grabbed a frame, null when running in simulated-viewfinder mode. */
  pages: Array<string | null>;
  surface: EntrySurface;
  actions: ReviewedAction[];
  /** "This is wrong" guardrail flag for the current capture. */
  markedWrong: boolean;
  /** Captures saved through Key Docs (prototype: in-memory). */
  savedCaptures: SavedCapture[];
  /** `${captureId}:${actionId}` keys of completed actions. */
  completedActionKeys: string[];
  /** Instrumentation log (the Mini PRD event schema, fired live). */
  events: TrackedEvent[];
  /** When set, Capture opens this picker on mount (journey-prompt deep entry). */
  autoOpenPicker: 'gallery' | 'file' | null;
  /** Carer asked for the care manager this session (visible to CAM strawman). */
  camContactRequested: boolean;

  setEntrySurface: (surface: EntrySurface) => void;
  setAutoOpenPicker: (picker: 'gallery' | 'file' | null) => void;
  requestCamContact: () => void;
  startPhotoSession: () => void;
  addPage: (thumb: string | null) => void;
  removePage: (index: number) => void;
  selectDocument: (doc: CapturedDocument, method: CaptureMethod) => void;
  markWrong: () => void;
  decideAction: (id: string, decision: ActionDecision) => void;
  tweakAction: (id: string, title: string) => void;
  /** Pass false to save the document with NO actions, discarding decisions. */
  saveCapture: (includeActions?: boolean) => SavedCapture | null;
  deleteCapture: (captureId: string) => void;
  toggleActionComplete: (captureId: string, actionId: string) => void;
  track: (name: string, payload: TrackedEvent['payload']) => void;
  resetFlow: () => void;
  /** Full prototype reset for team pressure-testing sessions. */
  resetAll: () => void;
}

const CaptureFlowContext = createContext<CaptureFlowState | null>(null);

const toReviewedActions = (doc: CapturedDocument): ReviewedAction[] =>
  doc.suggestedActions.map((a) => ({
    ...a,
    decision: 'pending',
    isTweaked: false,
  }));

interface CaptureFlowProviderProps {
  children: React.ReactNode;
  /** Seed state - used by Storybook stories to render mid-flow screens. */
  initialDoc?: CapturedDocument;
  initialMethod?: CaptureMethod;
  initialSavedCaptures?: SavedCapture[];
}

export const CaptureFlowProvider = ({
  children,
  initialDoc,
  initialMethod = 'photo',
  initialSavedCaptures = [],
}: CaptureFlowProviderProps) => {
  const [doc, setDoc] = useState<CapturedDocument | null>(initialDoc ?? null);
  const [method, setMethod] = useState<CaptureMethod>(initialMethod);
  const [pages, setPages] = useState<Array<string | null>>([]);
  const [surface, setSurface] = useState<EntrySurface>('care');
  const [actions, setActions] = useState<ReviewedAction[]>(
    initialDoc ? toReviewedActions(initialDoc) : [],
  );
  const [markedWrong, setMarkedWrong] = useState(false);
  const [savedCaptures, setSavedCaptures] =
    useState<SavedCapture[]>(initialSavedCaptures);
  const [completedActionKeys, setCompletedActionKeys] = useState<string[]>([]);
  const [events, setEvents] = useState<TrackedEvent[]>([]);
  const [autoOpenPicker, setAutoOpenPickerState] = useState<
    'gallery' | 'file' | null
  >(null);
  const [camContactRequested, setCamContactRequested] = useState(false);
  const captureCounter = useRef(initialSavedCaptures.length);

  const value = useMemo<CaptureFlowState>(() => {
    const track = (name: string, payload: TrackedEvent['payload']) => {
      setEvents((prev) => [
        { name, payload, at: new Date().toLocaleTimeString() },
        ...prev,
      ]);
    };

    return {
      doc,
      method,
      pages,
      surface,
      actions,
      markedWrong,
      savedCaptures,
      completedActionKeys,
      events,
      autoOpenPicker,
      camContactRequested,
      track,

      setEntrySurface: (next) => setSurface(next),
      setAutoOpenPicker: (picker) => setAutoOpenPickerState(picker),
      requestCamContact: () => {
        setCamContactRequested(true);
        track('CAM Contact Requested (proposed)', { surface });
      },
      startPhotoSession: () => {
        setDoc(null);
        setMethod('photo');
        setPages([]);
        setActions([]);
        setMarkedWrong(false);
      },
      addPage: (thumb) => setPages((prev) => [...prev, thumb]),
      removePage: (index) =>
        setPages((prev) => prev.filter((_, i) => i !== index)),
      selectDocument: (nextDoc, nextMethod) => {
        setDoc(nextDoc);
        setMethod(nextMethod);
        setActions(toReviewedActions(nextDoc));
        setMarkedWrong(false);
      },
      markWrong: () => {
        setMarkedWrong(true);
        if (doc) track('Capture Marked Wrong', { type: doc.classification });
      },
      decideAction: (id, decision) => {
        setActions((prev) =>
          prev.map((a) => (a.id === id ? { ...a, decision } : a)),
        );
        if (doc && decision !== 'pending') {
          track(
            decision === 'accepted'
              ? 'Capture Action Accepted'
              : 'Capture Action Rejected',
            { type: doc.classification, action_id: id },
          );
        }
      },
      tweakAction: (id, title) => {
        setActions((prev) =>
          prev.map((a) =>
            a.id === id
              ? { ...a, title, isTweaked: true, decision: 'accepted' }
              : a,
          ),
        );
        if (doc) {
          track('Capture Action Tweaked', {
            type: doc.classification,
            action_id: id,
          });
        }
      },
      saveCapture: (includeActions = true) => {
        if (!doc) return null;
        captureCounter.current += 1;
        const saved: SavedCapture = {
          id: `capture-${captureCounter.current}`,
          doc,
          method,
          acceptedActions: includeActions
            ? actions.filter((a) => a.decision === 'accepted')
            : [],
          savedAt: 'Just now',
          markedWrong,
        };
        setSavedCaptures((prev) => [saved, ...prev]);
        track('Capture Result Saved', { type: doc.classification });
        return saved;
      },
      deleteCapture: (captureId) => {
        const target = savedCaptures.find((c) => c.id === captureId);
        setSavedCaptures((prev) => prev.filter((c) => c.id !== captureId));
        setCompletedActionKeys((prev) =>
          prev.filter((key) => !key.startsWith(`${captureId}:`)),
        );
        if (target) {
          track('Document Deleted (proposed)', {
            type: target.doc.classification,
          });
        }
      },
      toggleActionComplete: (captureId, actionId) => {
        const key = `${captureId}:${actionId}`;
        setCompletedActionKeys((prev) => {
          const completing = !prev.includes(key);
          if (completing) {
            const capture = savedCaptures.find((c) => c.id === captureId);
            if (capture) {
              track('Capture Action Completed', {
                type: capture.doc.classification,
                action_id: actionId,
              });
            }
            return [...prev, key];
          }
          return prev.filter((k) => k !== key);
        });
      },
      resetFlow: () => {
        setDoc(null);
        setPages([]);
        setActions([]);
        setMarkedWrong(false);
      },
      resetAll: () => {
        setDoc(null);
        setPages([]);
        setActions([]);
        setMarkedWrong(false);
        setSavedCaptures([]);
        setCompletedActionKeys([]);
        setEvents([]);
        setAutoOpenPickerState(null);
        setCamContactRequested(false);
        captureCounter.current = 0;
      },
    };
  }, [
    doc,
    method,
    pages,
    surface,
    actions,
    markedWrong,
    savedCaptures,
    completedActionKeys,
    events,
    autoOpenPicker,
    camContactRequested,
  ]);

  return (
    <CaptureFlowContext.Provider value={value}>
      {children}
    </CaptureFlowContext.Provider>
  );
};

export const useCaptureFlow = () => {
  const ctx = useContext(CaptureFlowContext);
  if (!ctx) {
    throw new Error('useCaptureFlow must be used within CaptureFlowProvider');
  }
  return ctx;
};
